/* ==========================================================================
   CONFIGURAÇÕES E ESQUEMAS DO CMS (admin-collections.config.ts)
   ========================================================================== */

/**
 * Representa uma linha genérica de dados retornada do banco de dados (Cloudflare D1 / MySQL).
 */
export type Scalar = string | number | boolean | null;
export type Row = Record<string, Scalar | undefined>;

/**
 * Tipos de campos de entrada suportados dinamicamente pelos formulários do CMS.
 */
export type FieldType = 'text' | 'textarea' | 'number' | 'url' | 'checkbox';

/**
 * Estrutura descritiva de uma coluna no formulário de edição/criação.
 */
export interface Field {
  /** Nome da chave da coluna no banco de dados */
  key: string;
  /** Rótulo legível para o usuário na interface */
  label: string;
  /** Tipo de campo a ser renderizado */
  type?: FieldType;
}

/**
 * Metadados de configuração de cada coleção (tabela) do CMS.
 */
export interface CollectionMeta {
  /** Título legível da seção */
  title: string;
  /** Lista de campos editáveis da tabela */
  fields: Field[];
}

/**
 * Chaves de identificação das coleções CRUD padrão.
 */
export type CollectionKey =
  | 'services'
  | 'projects'
  | 'metrics'
  | 'clients'
  | 'partners'
  | 'faqs'
  | 'menu_items';

/**
 * Chaves de todas as abas navegáveis do painel administrativo.
 */
export type AdminTabKey = CollectionKey | 'settings' | 'messages' | 'users';

/**
 * Dicionário central de esquemas e campos das tabelas gerenciadas pelo CMS.
 */
export const collections: Record<CollectionKey, CollectionMeta> = {
  services: {
    title: 'Serviços',
    fields: [
      { key: 'slug', label: 'Slug' },
      { key: 'name', label: 'Nome' },
      { key: 'short_description', label: 'Resumo', type: 'textarea' },
      { key: 'description', label: 'Descrição', type: 'textarea' },
      { key: 'icon', label: 'Ícone' },
      { key: 'sort_order', label: 'Ordem', type: 'number' },
      { key: 'active', label: 'Publicado', type: 'checkbox' },
    ],
  },
  projects: {
    title: 'Portfólio',
    fields: [
      { key: 'slug', label: 'Slug' },
      { key: 'name', label: 'Nome' },
      { key: 'category', label: 'Categoria' },
      { key: 'description', label: 'Descrição', type: 'textarea' },
      { key: 'url', label: 'URL', type: 'url' },
      { key: 'image_url', label: 'Imagem', type: 'url' },
      { key: 'sort_order', label: 'Ordem', type: 'number' },
      { key: 'active', label: 'Publicado', type: 'checkbox' },
    ],
  },
  metrics: {
    title: 'Métricas',
    fields: [
      { key: 'label', label: 'Rótulo' },
      { key: 'value_text', label: 'Valor' },
      { key: 'description', label: 'Descrição', type: 'textarea' },
      { key: 'sort_order', label: 'Ordem', type: 'number' },
      { key: 'active', label: 'Exibir', type: 'checkbox' },
    ],
  },
  clients: {
    title: 'Clientes',
    fields: [
      { key: 'name', label: 'Nome' },
      { key: 'description', label: 'Descrição', type: 'textarea' },
      { key: 'website_url', label: 'Site', type: 'url' },
      { key: 'logo_url', label: 'Logo', type: 'url' },
      { key: 'sort_order', label: 'Ordem', type: 'number' },
      { key: 'active', label: 'Exibir', type: 'checkbox' },
    ],
  },
  partners: {
    title: 'Parceiros',
    fields: [
      { key: 'name', label: 'Nome' },
      { key: 'description', label: 'Descrição', type: 'textarea' },
      { key: 'website_url', label: 'Site', type: 'url' },
      { key: 'logo_url', label: 'Logo', type: 'url' },
      { key: 'sort_order', label: 'Ordem', type: 'number' },
      { key: 'active', label: 'Exibir', type: 'checkbox' },
    ],
  },
  faqs: {
    title: 'FAQ',
    fields: [
      { key: 'question', label: 'Pergunta' },
      { key: 'answer', label: 'Resposta', type: 'textarea' },
      { key: 'sort_order', label: 'Ordem', type: 'number' },
      { key: 'active', label: 'Exibir', type: 'checkbox' },
    ],
  },
  menu_items: {
    title: 'Menu e submenus',
    fields: [
      { key: 'parent_id', label: 'ID do menu pai' },
      { key: 'label', label: 'Texto' },
      { key: 'href', label: 'Link' },
      { key: 'target', label: 'Destino' },
      { key: 'icon', label: 'Ícone' },
      { key: 'sort_order', label: 'Ordem', type: 'number' },
      { key: 'active', label: 'Exibir', type: 'checkbox' },
    ],
  },
};